import type { CompressionPreset } from "./pdf-types";

let runnerPromise: Promise<{
  run: (input: {
    inputs: Record<string, Uint8Array>;
    args: string[];
    outputs: string[];
  }) => Promise<{ outputs: Record<string, Uint8Array> }>;
}> | null = null;

async function getRunner() {
  if (!runnerPromise) {
    const { createQpdfRunner } = await import("qpdf-run");
    runnerPromise = createQpdfRunner({
      workerUrl: new URL("qpdf-run/worker", import.meta.url).href,
      qpdfJsUrl: new URL("qpdf-run/qpdf.js", import.meta.url).href,
      wasmUrl: new URL("qpdf-run/qpdf.wasm", import.meta.url).href,
    });
  }
  return runnerPromise;
}

async function compressWithPdfLib(file: ArrayBuffer): Promise<ArrayBuffer> {
  const { PDFDocument } = await import("pdf-lib");
  const document = await PDFDocument.load(file);
  const bytes = await document.save({ useObjectStreams: true });
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

export async function compressPdfWithQpdf(
  file: ArrayBuffer,
  preset: CompressionPreset = "balanced",
): Promise<ArrayBuffer> {
  try {
    const qpdf = await getRunner();
    const args =
      preset === "lossless"
        ? ["--object-streams=generate", "--stream-data=compress", "--compress-streams=y", "--linearize"]
        : preset === "maximum"
          ? [
              "--object-streams=generate",
              "--stream-data=compress",
              "--compress-streams=y",
              "--optimize-images",
              "--jpeg-quality=60",
              "--linearize",
            ]
          : [
              "--object-streams=generate",
              "--stream-data=compress",
              "--compress-streams=y",
              "--optimize-images",
              "--jpeg-quality=75",
              "--linearize",
            ];
    const result = await qpdf.run({
      inputs: { "input.pdf": new Uint8Array(file) },
      args: [...args, "--", "input.pdf", "output.pdf"],
      outputs: ["output.pdf"],
    });
    const output = result.outputs["output.pdf"];
    if (!output) throw new Error("The qpdf worker did not return an optimized PDF.");
    return output.buffer.slice(output.byteOffset, output.byteOffset + output.byteLength) as ArrayBuffer;
  } catch {
    return compressWithPdfLib(file);
  }
}
